import { useState, useRef, type FormEvent, type KeyboardEvent, type ClipboardEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, ArrowLeft } from "lucide-react";
import { apiPost } from "../lib/api";
import { useAuth } from "../lib/auth";
import { storeWorkspaces, type WorkspaceSession, type WorkspaceSummary } from "../lib/workspaces";

export function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [verifying, setVerifying] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await apiPost("/auth/login", { email });
      if (res.success) {
        setSent(true);
      } else if (res.error?.key === "USER_NOT_FOUND") {
        navigate("/register", { state: { email } });
      } else {
        setError(res.error?.message || "Failed to send magic link");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  }

  async function verifyCode(digits: string[]) {
    const code = digits.join("");
    if (code.length !== 6) return;
    setVerifying(true);
    setError(null);
    try {
      const res = await apiPost<WorkspaceSession & { needsRegistration?: boolean; selectionToken?: string; workspaces?: WorkspaceSummary[] }>("/auth/otp/verify", { email, code });
      if (res.success && res.data?.selectionToken && res.data.workspaces) {
        storeWorkspaces(res.data.workspaces);
        sessionStorage.setItem("vms_selection_token", res.data.selectionToken);
        sessionStorage.setItem("vms_selection_workspaces", JSON.stringify(res.data.workspaces));
        navigate("/workspaces", { replace: true });
      } else if (res.success && res.data?.token && res.data.tenant) {
        login(res.data);
        navigate(`/w/${res.data.tenant.slug}/checkins`, { replace: true });
      } else if (res.success && res.data?.needsRegistration) {
        setError("No account found. Please register first.");
        setVerifying(false);
      } else {
        setError(res.error?.message || "Invalid code");
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
        setVerifying(false);
      }
    } catch {
      setError("Network error");
      setVerifying(false);
    }
  }

  function handleOtpChange(index: number, value: string) {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (value && index === 5) {
      verifyCode(next);
    }
  }

  function handleOtpKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  function handleOtpPaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const next = [...otp];
    for (let i = 0; i < 6; i++) next[i] = pasted[i] || "";
    setOtp(next);
    if (pasted.length === 6) {
      verifyCode(next);
    } else {
      inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    }
  }

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-8 text-center animate-[fadeIn_0.3s_ease-out]">
            <div className="mx-auto w-14 h-14 rounded-full bg-brand-50 flex items-center justify-center mb-5">
              <Mail className="w-6 h-6 text-brand-500" />
            </div>
            <h1 className="text-2xl font-semibold text-stone-900 mb-2 font-display">Check your email</h1>
            <p className="text-sm text-stone-500 leading-relaxed mb-6">
              We sent a sign-in code to{" "}
              <span className="font-medium text-stone-700">{email}</span>.
              Enter the 6-digit code below.
            </p>

            <div className="flex justify-center gap-2 mb-4">
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => { inputRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  onPaste={i === 0 ? handleOtpPaste : undefined}
                  disabled={verifying}
                  className="w-10 h-12 text-center text-lg font-semibold border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:opacity-50"
                  autoFocus={i === 0}
                />
              ))}
            </div>

            {verifying && <p className="text-sm text-stone-500 mb-3">Verifying...</p>}
            {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mb-3">{error}</p>}

            <p className="text-xs text-stone-400 mb-4">Or click the link in the email</p>

            <div className="pt-4 border-t border-stone-100">
              <button
                onClick={() => { setSent(false); setEmail(""); setOtp(["", "", "", "", "", ""]); setError(null); }}
                className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 font-medium transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Use a different email
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-6">
          <div className="flex items-center gap-2">
            <img src="/singvms-seal.svg" alt="SingVMS" className="w-9 h-9" />
            <span className="text-xl font-bold text-stone-900 font-display">SingVMS</span>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-8">
          <h1 className="text-2xl font-semibold text-stone-900 mb-1 font-display">Sign in</h1>
          <p className="text-sm text-stone-500 mb-6">Enter your email to receive a sign-in link</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="you@company.com"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {loading ? "Sending..." : "Send magic link"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-stone-500">
            Don't have an account?{" "}
            <Link to="/register" className="text-brand-500 hover:underline font-medium">
              Register
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
